import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Alert, App, Button, DatePicker, Drawer, Form, Input, InputNumber, Segmented, Select, Space, Switch, Tag } from 'antd';
import dayjs from 'dayjs';
import { api } from '../api';
import { DataTable, ErrorNotice, PageHeader, Panel, Person, PlanTag, SubscriptionTag } from '../components';
import { countLabel, daysBetween, formatDate, formatRupees, mobileLabel, subscriptionState, todayIso, useLoad } from '../lib';
/** The plan Select's choice for "type another plan name". */
const OTHER = '__other__';
const IN_FILTER = {
    all: ['scheduled', 'active', 'expiring', 'grace', 'expired'],
    current: ['scheduled', 'active', 'expiring'],
    attention: ['expiring', 'grace'],
    expired: ['expired']
};
export function SubscriptionsPage() {
    const { message } = App.useApp();
    const [filter, setFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const { data, error, loading, reload } = useLoad(async () => {
        const [subscriptions, accounts, plans] = await Promise.all([
            api('/v1/admin/subscriptions'),
            api('/v1/admin/accounts'),
            api('/v1/admin/plans')
        ]);
        return { rows: subscriptions.subscriptions, accounts: accounts.accounts.filter((account) => !account.disabledAt), plans: plans.plans };
    });
    const today = todayIso();
    const rows = data?.rows ?? [];
    const accounts = data?.accounts ?? [];
    const plans = data?.plans ?? [];
    const planNames = new Map(plans.map((plan) => [plan.code, plan.name]));
    const mobiles = new Map(accounts.map((account) => [account.id, mobileLabel(account.mobile)]));
    const shown = rows.filter((row) => IN_FILTER[filter].includes(subscriptionState(row, today)));
    const count = (key) => rows.filter((row) => IN_FILTER[key].includes(subscriptionState(row, today))).length;
    /** Choosing a plan fills in its computers, remote access and length; they can still be changed. */
    function choosePlan(code) {
        const plan = plans.find((item) => item.code === code);
        if (!plan)
            return;
        const from = form.getFieldValue('period')?.[0] ?? dayjs();
        form.setFieldsValue({
            devices: plan.devices,
            remote: plan.entitlements.includes('remote'),
            period: [from, from.add(plan.durationMonths, 'month').subtract(1, 'day')]
        });
    }
    function openIssue() {
        setOpen(true);
        // The first active plan, so the limits start from a real plan.
        const first = plans.find((plan) => plan.active);
        if (first)
            window.setTimeout(() => {
                form.setFieldValue('plan', first.code);
                choosePlan(first.code);
            });
    }
    async function issue(values) {
        setSaving(true);
        try {
            const plan = values.plan === OTHER ? (values.otherPlan ?? '').trim() : values.plan;
            await api('/v1/admin/subscriptions', {
                method: 'POST',
                body: JSON.stringify({
                    accountId: values.accountId,
                    plan,
                    entitlements: values.remote ? ['remote'] : [],
                    // The API always takes a session limit; without remote access it is not used.
                    limits: { remoteSessions: values.remoteSessions ?? 1, devices: values.devices },
                    validFrom: values.period[0].format('YYYY-MM-DD'),
                    validUntil: values.period[1].format('YYYY-MM-DD'),
                    graceDays: values.graceDays
                })
            });
            message.success('Subscription issued. The customer signs in with their mobile number to use it.');
            setOpen(false);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not issue the subscription');
        }
        finally {
            setSaving(false);
        }
    }
    return (<>
      <PageHeader title="Subscriptions" description="What each account may use and until when. Customers buy plans in the account panel; issue one by hand for a complimentary or offline-paid plan." actions={<Button type="primary" icon={<PlusOutlined />} onClick={openIssue}>
            Issue by hand
          </Button>}/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <div className="toolbar">
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'all', label: countLabel('All', rows.length, data !== null) },
            { value: 'current', label: countLabel('Current', count('current'), data !== null) },
            { value: 'attention', label: countLabel('Needs renewal', count('attention'), data !== null) },
            { value: 'expired', label: countLabel('Expired', count('expired'), data !== null) }
        ]}/>
        </div>
        <DataTable rowKey="id" loading={loading} dataSource={shown} pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 1000 }} locale={{ emptyText: rows.length ? 'No subscriptions match.' : 'No subscriptions yet. They appear when a customer buys a plan.' }} columns={[
            {
                title: 'Account',
                skeleton: 'person',
                render: (_value, row) => <Person name={row.accountName} detail={[mobiles.get(row.accountId), row.accountEmail].filter(Boolean).join(' · ') || undefined}/>
            },
            { title: 'Plan', dataIndex: 'plan', width: 140, skeleton: 'tag', render: (value) => <PlanTag plan={value} name={planNames.get(value)}/> },
            {
                title: 'Includes',
                width: 220,
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  {row.entitlements.includes('remote') ? <Tag variant="filled">Remote access</Tag> : <span className="cell-sub">Local only</span>}
                  <span className="cell-sub">
                    {row.activeDevices ? 'Signed in on a computer' : 'No computer signed in'}
                    {row.entitlements.includes('remote') && ` · ${row.limits.remoteSessions} remote ${row.limits.remoteSessions === 1 ? 'session' : 'sessions'}`}
                  </span>
                </span>)
            },
            {
                title: 'Period',
                width: 230,
                skeleton: 'lines',
                render: (_value, row) => {
                    const left = daysBetween(today, row.validUntil);
                    return (<span>
                    {formatDate(row.validFrom)} – {formatDate(row.validUntil)}
                    <span className="cell-sub">
                      {left >= 0 ? `${left} ${left === 1 ? 'day' : 'days'} left` : `Ended ${-left} days ago`} · {row.graceDays} days grace
                    </span>
                  </span>);
                }
            },
            { title: 'Status', width: 150, skeleton: 'tag', render: (_value, row) => <SubscriptionTag state={subscriptionState(row, today)}/> }
        ]}/>
      </Panel>

      <Drawer title="Issue a subscription by hand" open={open} onClose={() => setOpen(false)} size={480} destroyOnHidden footer={<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} disabled={accounts.length === 0} onClick={() => form.submit()}>
              Issue subscription
            </Button>
          </Space>}>
        <Alert type="info" showIcon title="For complimentary or offline-paid plans" description="No payment or invoice is recorded. Customers who pay online get their subscription on their own." style={{ marginBottom: 20 }}/>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={issue} initialValues={{ remote: false, remoteSessions: 1, devices: 1, period: [dayjs(), dayjs().add(1, 'year').subtract(1, 'day')], graceDays: 15 }}>
          <Form.Item label="Account" name="accountId" rules={[{ required: true, message: 'Choose an account.' }]}>
            <Select showSearch={{ optionFilterProp: 'label' }} placeholder={accounts.length ? 'Search by name or mobile' : 'Create an account first'} options={accounts.map((account) => ({ value: account.id, label: [account.name, mobileLabel(account.mobile) || account.email].filter(Boolean).join(' · ') }))}/>
          </Form.Item>
          <Form.Item label="Plan" name="plan" rules={[{ required: true, message: 'Choose a plan.' }]} extra="Fills in the computers, remote access and dates from the plan.">
            <Select aria-label="Plan" placeholder="Choose a plan" onChange={(value) => choosePlan(value)} options={[
            ...plans.map((plan) => ({
                value: plan.code,
                label: `${plan.name} · ${plan.code} · ${formatRupees(plan.pricePaise)}${plan.active ? '' : ' (inactive)'}`
            })),
            { value: OTHER, label: 'Another plan name…' }
        ]}/>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.plan !== next.plan}>
            {({ getFieldValue }) => getFieldValue('plan') === OTHER && (<Form.Item label="Plan name" name="otherPlan" rules={[{ required: true, whitespace: true, max: 40, message: 'Type a plan name of up to 40 characters.' }]} extra="Kept on the subscription, e.g. complimentary.">
                  <Input placeholder="complimentary" autoFocus/>
                </Form.Item>)}
          </Form.Item>
          <Form.Item label="Valid" name="period" rules={[{ required: true, message: 'Choose the dates.' }]}>
            <DatePicker.RangePicker placeholder={['Valid from', 'Valid until']} style={{ width: '100%' }} format="DD MMM YYYY" allowClear={false}/>
          </Form.Item>
          <div className="form-row">
            {/* An account is signed in on one computer at a time. */}
            <Form.Item name="devices" hidden>
              <InputNumber/>
            </Form.Item>
            <Form.Item label="Grace days" name="graceDays" rules={[{ required: true }]} extra="Keeps working after the end date.">
              <InputNumber placeholder="15" min={0} max={90} style={{ width: '100%' }}/>
            </Form.Item>
          </div>
          <div className="switch-row">
            <div>
              <strong>Remote access</strong>
              <span>Lets the account open its books from another computer.</span>
            </div>
            <Form.Item name="remote" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </div>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.remote !== next.remote}>
            {({ getFieldValue }) => getFieldValue('remote') && (<Form.Item label="Remote sessions at once" name="remoteSessions" rules={[{ required: true }]}>
                  <InputNumber placeholder="1" min={1} max={100} style={{ width: '100%' }}/>
                </Form.Item>)}
          </Form.Item>
        </Form>
      </Drawer>
    </>);
}
